"use client";

import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { Logger } from "@shared/utils/logger";
import { createSocket } from "@client/utils/socket-dynamic";
import { CALL_SERVER_EVENTS } from "@shared/constants/callEvents";
import type { CallStateSyncPayload } from "@shared/constants/callEvents";
import type { Socket } from "@client/utils/socket-dynamic";

// ─── Types ────────────────────────────────────────────────────────────────────

interface CallSocketContextValue {
  socket: Socket | null;
  callId: string | null;
  chatId: string | null;
  isCallPage: boolean;
}

interface CallSocketProviderProps {
  children: React.ReactNode;
  roomName: string;
}

// ─── Module-level singleton (survives React re-renders) ───────────────────────

let socketInstance: Socket | null = null;
let setSocketRef: ((s: Socket | null) => void) | null = null;

export function disconnectCallSocket(): void {
  try {
    if (socketInstance) {
      socketInstance.removeAllListeners();
      socketInstance.disconnect();
    }
  } catch (err) {
    Logger.error("CallSocketProvider", "Error while disconnecting socket", err);
  } finally {
    socketInstance = null;
    if (setSocketRef) setSocketRef(null);
  }
}

// ─── Context ─────────────────────────────────────────────────────────────────

const CallSocketContext = createContext<CallSocketContextValue | undefined>(undefined);

// ─── Provider ────────────────────────────────────────────────────────────────

export const CallSocketProvider: React.FC<CallSocketProviderProps> = ({ children, roomName }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [callId, setCallId] = useState<string | null>(null);
  const [chatId, setChatId] = useState<string | null>(null);
  const sessionRef = useRef<string | null>(null);

  // Expose state setter so disconnectCallSocket() can clear provider state
  useEffect(() => {
    setSocketRef = setSocket;
    return () => {
      if (setSocketRef === setSocket) setSocketRef = null;
    };
  }, [setSocket]);

  // ── Fetch session cookie via /api/session ──────────────────────────────────
  useEffect(() => {
    const fetchSession = async () => {
      try {
        const res = await fetch("/api/session");
        if (!res.ok) return;
        const data = (await res.json()) as { session: string | null };
        sessionRef.current = data.session;
      } catch (err) {
        Logger.error("CallSocketProvider", "Failed to fetch session", err);
      }
    };
    fetchSession();
  }, []);

  // ── Socket connection lifecycle ────────────────────────────────────────────
  useEffect(() => {
    const endpoint = process.env.NEXT_PUBLIC_SOCKET_ENDPOINT;
    if (!endpoint) {
      Logger.warn("CallSocketProvider", "NEXT_PUBLIC_SOCKET_ENDPOINT is not configured");
      return;
    }

    // Wait briefly for the session fetch to complete, then connect
    const connectWithSession = async () => {
      // Small delay to let the session fetch complete on first render
      await new Promise<void>((resolve) => {
        const check = () => {
          if (sessionRef.current !== null) {
            resolve();
          } else {
            setTimeout(check, 50);
          }
        };
        // Resolve after max 2s even if session is unavailable (guest mode)
        setTimeout(resolve, 2000);
        check();
      });

      // Reuse existing connected socket if available
      if (socketInstance?.connected) {
        setSocket(socketInstance);
        return;
      }

      // Clean up stale disconnected instance
      if (socketInstance) {
        socketInstance.removeAllListeners();
        socketInstance.disconnect();
        socketInstance = null;
      }

      socketInstance = await createSocket(endpoint, {
        transports: ["polling", "websocket"],
        auth: {
          token: sessionRef.current
        },
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 10000,
        query: { roomName }
      });

      socketInstance.on("connect", () => {
        Logger.info("CallSocketProvider", `Socket connected (id=${socketInstance?.id})`);
        setSocket(socketInstance);
      });

      socketInstance.on("connect_error", (error) => {
        Logger.error("CallSocketProvider", "Socket connection error", error.message);
      });

      socketInstance.on("disconnect", (reason) => {
        Logger.info("CallSocketProvider", `Socket disconnected (reason=${reason})`);
        // Server-initiated disconnect: reset so a fresh socket is created next time
        if (reason === "io server disconnect") {
          socketInstance = null;
          setSocket(null);
        }
      });

      // Populate callId + chatId from call_state_sync (emitted by server on connect
      // for any active call session this user is part of)
      socketInstance.on(CALL_SERVER_EVENTS.CALL_STATE_SYNC, (payload: CallStateSyncPayload) => {
        if (payload.roomName === roomName) {
          setCallId(payload.callId);
          setChatId(payload.chatId);
          Logger.info("CallSocketProvider", `call_state_sync — callId=${payload.callId} chatId=${payload.chatId}`);
        }
      });
    };

    connectWithSession();

    return () => {
      // Do not disconnect on unmount — disconnectCallSocket() is the explicit teardown.
      // (It's called by useCallSocketEvents when the user leaves the room)
    };
  }, [roomName]);

  return (
    <CallSocketContext.Provider value={{ socket, callId, chatId, isCallPage: true }}>
      {children}
    </CallSocketContext.Provider>
  );
};

const defaultCallSocketContext: CallSocketContextValue = {
  socket: null,
  callId: null,
  chatId: null,
  isCallPage: false
};

// ─── Hooks ────────────────────────────────────────────────────────────────────

export const useOptionalCallSocket = (): CallSocketContextValue => {
  return useContext(CallSocketContext) ?? defaultCallSocketContext;
};

export const useCallSocket = (): CallSocketContextValue => {
  const ctx = useContext(CallSocketContext);
  if (!ctx) {
    throw new Error("useCallSocket must be used within a CallSocketProvider");
  }
  return ctx;
};
